import * as Api from "@/api";
import { setSettings } from "@/store/reducers/auth";
import { setProgressBar } from "@/store/reducers/ui";

import { jwtDecode } from "jwt-decode";

import { sippy, version } from "../../package";
import store from "../store";

/**
 * Parses the Mac returned by the API
 *
 * @param  {string} mac      A mac address
 *
 * @return {string}          The formated mac address
 */
export const formatToMac = (mac) =>
  mac
    ? mac
        .toString(16)
        .match(/.{1,2}/g)
        .join(":")
        .toUpperCase()
    : mac;

/**
 * Parses the CallerId returned by the API
 *
 * @param  {string} name      Asterisk CallerId
 *
 * @return {string}          The formated callername
 */
export const formatCallerName = (name) => name.slice(0, name.indexOf("<") - 1).replace(/"/g, "");

/**
 * Toggles the progress bar in the app bar
 *
 * @return {object}          The parsed JSON, status from the response
 */
export const toggleLoadingState = (state = false) => {
  store.dispatch(setProgressBar(state));
};

/**
 * Authenticate the token and current user
 *
 * @return {boolean}          The authenticated state
 */
export const authenticate = () => {
  const decoded = decodeJWT(store.getState().auth.jwtToken);
  const auth = store.getState().auth;

  return auth?.id && auth?.id === decoded?.id;
};

/**
 * Authenticate the token and current user
 *
 * @param  {string} token JSON token
 *
 * @return {object}          The decoded token
 */
export const decodeJWT = (token) => {
  if (token != null) {
    return jwtDecode(token);
  }
};

/**
 * Find the name of a user
 *
 * @param  {string}  extension:    The user extension name (pbx-1-200)
 * @param  {array}   userList:  List of all users
 *
 * @return {object}              Returns the name or the extension if not found
 */
export const getUserName = (extension, userList) => {
  let username = "";

  if (extension === null || userList === undefined) {
    return "";
  }

  // Starts with number and not the context
  if (extension.match(/^\d/)) {
    const user = userList.find((user) => user.exten === extension);
    username = user ? user.name : extension.split("-").slice(2, 3);
  }

  const user = userList.find((user) => user.name === extension);
  username = user ? user.name : extension.split("-").slice(2, 3);

  return username.length >= 1 ? username : "";
};

/**
 * Converts a name to its first and last initial
 *
 * @param  {string}  name:    The name of a person
 *
 * @return {string}  Returns initials
 */
export const getInitials = (name) => {
  let initials = name?.match(/\b\w/g) || [];
  initials = ((initials.shift() || "") + (initials.pop() || "")).toUpperCase();

  return initials;
};

/**
 * Moves an object in an array by index
 *
 * @param  {array}  array:    The array in which elements have to be moved
 * @param  {int}    from:     The location of the element at the start
 * @param  {int}    to:       The location of the element at the end
 *
 * @return {array}  Returns an array
 */
export const moveByIndex = (array, from, to) => {
  const movedItem = array.find((item, index) => index === from);
  const moveForward = from > to;

  return [
    ...array.filter((item, index) => (moveForward ? index < to : index <= to) && index !== from),
    movedItem,
    ...array.filter((item, index) => (moveForward ? index >= to : index > to) && index !== from),
  ];
};

/**
 * Returns value name of a given extension
 *
 * @param  {int} accessLevel:          The access level
 *
 * @return {string} Returns the name of the extention, or the extension if nothing was found
 */
export const getAccessLevel = (accessLevel) => {
  switch (accessLevel) {
    case 0:
      return "Admin";
    case 1:
      return "Partner";
    case 2:
      return "Customer";
    case 3:
      return "User";
    case 4:
      return "Viewer";
    default:
      return "Unknown";
  }
};

/**
 * Get current version
 */
export const getVersion = (format = null) => {
  let devVersion, devStage;

  if (version.indexOf("-") >= 0) {
    devVersion = version.substring(version.indexOf("-") + 1);
    devStage = devVersion.substring(0, devVersion.indexOf("."));
  }

  if (format === "devType") {
    return import.meta.env.SIPPY_ENV !== "docs" ? devStage : undefined;
  } else if (format === "major") {
    return version.substring(0, version.indexOf(".", version.indexOf(".") + 1));
  } else if (format === "main" || import.meta.env.SIPPY_ENV === "docs") {
    return version.indexOf("-") >= 0 ? version.substring(0, version.indexOf("-")) : version;
  } else {
    return version;
  }
};

/**
 * Get current date
 */
export const getVersionDate = () => sippy.date;

/**
 * Get an array
 */
export const createArray = (from, length) => {
  return Array.from(Array(length).keys(), (n) => n + from);
};

/**
 * Save a setting to the store and database
 *
 * @param {*} key
 * @param {*} value
 */
export const saveSetting = async (key, value) => {
  const version = getVersion("major");
  const settings = Object.assign({}, store.getState().auth.settings);
  settings[key] = value;

  await Promise.resolve(
    Api.putAccountSettings({
      ...settings,
      version: settings.version ?? version,
    }),
  ).then(() => {
    store.dispatch(setSettings(settings));
  });
};

export const implode = (arr, separator) => {
  if (arr.length === 1) {
    return arr[0];
  }
  return arr.slice(0, -1).join(separator) + ", and " + arr.slice(-1);
};

export const closest = (el, selector, rootNode) => {
  let element = el;
  while (element) {
    const isRoot = element === rootNode || element === document.body;
    if (isRoot || (element.nodeType === 1 && element.matches(selector))) {
      if (isRoot) {
        element = null;
      }
      break;
    }
    element = element.parentNode;
  }
  return element;
};

export const getScrollElement = (el) => {
  let element = el;
  do {
    const { overflow } = window.getComputedStyle(element);
    if (
      (overflow === "auto" || overflow === "scroll") &&
      element &&
      element.nodeType &&
      (element.offsetWidth < element.scrollWidth || element.offsetHeight < element.scrollHeight)
    ) {
      break;
    }
    if (!element || !element.nodeType || element === document.body) {
      element = null;
      break;
    }
    element = element.parentNode;
  } while (element);
  return element;
};

export const getDomIndex = (el, ignoreSelectors) => {
  return Array.from(el.parentNode.children)
    .filter((e) => (ignoreSelectors === "" ? true : !e.matches(ignoreSelectors)))
    .indexOf(el);
};
